/**
 * Copyright (c) Qwoach LLC 2020. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 7/17/20
 *
 */
import {SubscriptionStatusPaddle} from "@shared/Subscription/SubscriptionStatusPaddle";
import {Type, classToClass} from "@qwoach/class-transformer-wrapper"
import SubscriptionAddons from "@shared/Subscription/Addons/SubscriptionAddons";
import SubscriptionFeatures from "@shared/Subscription/Features/SubscriptionFeatures";
import dayjs from "dayjs"
import {AccountTypeEnum} from "@shared/Subscription/AccountTypeEnum.js";
import {ProductTypeEnum} from "@shared/Subscription/ProductTypeEnum.js";
import SubscriptionPricing from "@shared/Subscription/SubscriptionPricing";
import {SubscriptionDurationEnum} from "@shared/Subscription/SubscriptionDurationEnum";
import CurrencyItem from "@shared/Profile/CurrencyItem";
import FeatureDescription from "@shared/Subscription/FeatureDescription";
import DbBaseObject from "@shared/DbBaseObject";
import {ValuesEnum} from "@shared/Subscription/ValuesEnum";
import UsageStats from "@shared/UsageStats";



export default class UserSubscription extends DbBaseObject {

	name: string = null
	description: string = null
	cancelUrl: string = null
	billingUpdateUrl: string = null
	invoiceUrl: string = null
	productType: ProductTypeEnum = null

	alertType: string = null

	freeTrialDays: number = -1
	subscriptionId: string = null
	planId: string = null
	billingPeriod: string = SubscriptionDurationEnum.MONTH

	status: SubscriptionStatusPaddle = null
	validThroughTs: number = null
	nextRetry: number = 0
	nextPaymentAmount: number = null
	paymentTax: number = null
	currencyCode: string = null

	accountType: AccountTypeEnum = AccountTypeEnum.GENERAL

	price: number = 0
	pricePerMonth: number = 0

	@Type(() => FeatureDescription)
	featuresDescription: FeatureDescription[] = []

	@Type(() => SubscriptionPricing)
	pricing: SubscriptionPricing = new SubscriptionPricing()

	@Type(() => SubscriptionFeatures)
	features: SubscriptionFeatures = new SubscriptionFeatures()

	@Type(() => SubscriptionAddons)
	addons: SubscriptionAddons = new SubscriptionAddons()

	@Type(() => UsageStats)
	usageStats: UsageStats = new UsageStats()



	constructor() {
		super();
		this.id = "--subscription--"
	}

	regenerateId(){
		this.id = "--subscription--"
		return this
	}

	getCurrentSchemaVersion(){
		return 2
	}

	isSubscription(){
		return this.productType !== ProductTypeEnum.LIFETIME
	}

	isLifetime(){
		return this.productType === ProductTypeEnum.LIFETIME
	}

	isStarted(){
		return this.isActive() || this.isTrialing()
	}
	isActive(){
		return this.isAccessValid() && (this.status === SubscriptionStatusPaddle.ACTIVE || this.status === SubscriptionStatusPaddle.FREE || this.status === SubscriptionStatusPaddle.TRIALING_NO_CC)
	}
	isCancelled(){
		return this.status === SubscriptionStatusPaddle.CANCELLED || this.isPaused()
	}
	isPaused(){
		return this.status === SubscriptionStatusPaddle.PAUSED
	}
	isTrialing(){
		return this.isAccessValid()
			&& (this.status === SubscriptionStatusPaddle.TRIALING || this.status === SubscriptionStatusPaddle.TRIALING_NO_CC)
	}
	isPending(){
		return this.status === SubscriptionStatusPaddle.PENDING
	}
	isRejected(){
		return this.status === SubscriptionStatusPaddle.REJECTED
	}

	setCancelled(){
		this.status = SubscriptionStatusPaddle.CANCELLED
	}

	nextPaymentAmountFormatted(){
		let currencyItem = CurrencyItem.currencyByCode(this.currencyCode)
		return currencyItem.formatted(this.nextPaymentAmount)
	}
	nextPaymentAmountMonthlyFormatted(){
		let nextPaymentAmount = this.nextPaymentAmount
		if(this.billingPeriod === SubscriptionDurationEnum.YEAR){
			nextPaymentAmount = Math.floor(this.nextPaymentAmount / 12 * 100) / 100
		}
		let currencyItem = CurrencyItem.currencyByCode(this.currencyCode)
		return currencyItem.formatted(nextPaymentAmount)
	}

	nextRenewalTs(){
		let now = new Date().getTime()
		if(now < this.validThroughTs){
			return this.validThroughTs
		}
		if(now < this.nextRetry){
			return this.nextRetry
		}
		return 0
	}

	isNextRenewalInFuture(){
		let now = new Date().getTime()
		return now < this.nextRenewalTs()
	}

	hasRenewableSubscription(){
		let isPaused = this.status === SubscriptionStatusPaddle.PAUSED

		let canRenew = this.isNextRenewalInFuture() &&
				(  this.status === SubscriptionStatusPaddle.ACTIVE
				|| this.status === SubscriptionStatusPaddle.TRIALING
				|| this.status === SubscriptionStatusPaddle.PAST_DUE)

		return !this.isFreePlan() && (isPaused || canRenew)
	}

	isAccessValid(includeGracePeriod = true){
		const gracePeriodMs = 3 * 24 * 60 * 60 * 1000
		let validThroughTsFinal = this.validThroughTs
		if(includeGracePeriod){
			validThroughTsFinal += gracePeriodMs
		}
		let dateNow = new Date().getTime()
		return this.validThroughTs === 0 || validThroughTsFinal > dateNow
	}
	isAccessValidNoGracePeriod(){
		return this.isAccessValid(false)
	}

	isPastDue(){
		return this.status === SubscriptionStatusPaddle.PAST_DUE || !this.isAccessValid()
	}

	getMaxStorageSize(){
		let planMaxStorage = this.features.maxStorageSize || this.features.driveStorageFeature?.maxStorageSize || 0
		planMaxStorage += this.calculateAddonSpace()
		return planMaxStorage
	}

	calculateAddonSpace(){
		let planMaxStorage = 0
		if(this.addons.driveStorageAddons){
			this.addons.driveStorageAddons.forEach(addon => {
				addon.featureItems.forEach(feature => {
					if(feature.isStorageAddon()){
						planMaxStorage += feature.maxStorageSize || 0
					}
				})
			})
		}
		return planMaxStorage
	}

	isFeatureProspectDetailsDisabled(){
		return this.features.prospectDetailsDisabled === true
	}

	getSubscriptionById(planId){
		let res
		if(this.pricing?.yearly?.planId === planId){
			res = this.getYearlySubscription()
		} else if(this.pricing?.monthly?.planId === planId){
			res = this.getMonthlySubscription()
		}
		return res || this
	}

	isLastAlertTypeUpdate(){
		return this.alertType === "subscription_updated"
	}
	isLastAlertTypeCancel(){
		return this.alertType === "subscription_cancelled"
	}
	isLastAlertTypePaymentSuccess(){
		return this.alertType === "subscription_payment_succeeded"
	}
	isLastAlertTypePaymentFailed(){
		return this.alertType === "subscription_payment_failed"
	}
	isLastAlertTypeCreated(){
		return this.alertType === "subscription_created"
	}

	isNextRetryValid(){
		return this.nextRetry && this.nextRetry > new Date().getTime()
	}

	nextRetryFormatted(){
		return dayjs(this.nextRetry).format("MMM DD, YYYY")
	}

	validThroughFormatted(){
		return dayjs(this.validThroughTs).format("MMM DD, YYYY")
	}

	validThroughDays(){
		return dayjs(this.validThroughTs + 5 * 60 * 60 * 1000).diff(dayjs(), "days")
	}

	isFreePlan(){
		return this.getPriceAmount() === 0 || this.planId === "free" || this.planId === "850092" || this.isStudentPlan()
	}
	isPlanIdFree(){
		return this.planId === "free"
	}

	isStudentPlan(){
		return this.planId === "genogramStudentPlanId"
	}

	applyFreeTrial(options = {}){
		let freeTrialDays = this.freeTrialDays
		if(options.freeTrialDays != null){
			freeTrialDays = options.freeTrialDays
		}
		if(freeTrialDays && freeTrialDays > 0){
			this.validThroughTs = new Date().getTime() + freeTrialDays * 24 * 60 * 60 * 1000
			if(this.isFreePlan()){
				this.status = SubscriptionStatusPaddle.FREE
			} else if(options.no_cc){
				this.status = SubscriptionStatusPaddle.TRIALING_NO_CC
			} else {
				this.status = SubscriptionStatusPaddle.TRIALING
			}
		}
		return freeTrialDays
	}


	applyFeaturesFrom(plan){
		this.featuresDescription = plan.featuresDescription
		this.features = plan.features
	}

	getYearlySubscription(){
		let subscription = classToClass(this)

		if(subscription.billingPeriod === SubscriptionDurationEnum.YEAR){
			return subscription
		}
		if(this.pricing.yearly){
			subscription.name = this.name + " - Yearly"
			subscription.planId = this.pricing.yearly.planId
			subscription.billingPeriod = SubscriptionDurationEnum.YEAR
			subscription.price = this.pricing.yearly.price
			subscription.pricePerMonth = Math.floor(this.pricing.yearly.price / 12 * 100) / 100
			subscription.billingPeriodPerMonth = SubscriptionDurationEnum.MONTH
			subscription.billingNotePerMonth = "month (billed yearly)"
			subscription.currencyCode = this.pricing.yearly.currencyCode
			return subscription
		}
	}

	hasPlanId(planId){
		let planIdLocal = this.planId
		if(!Array.isArray(this.planId)){
			planIdLocal = [this.planId]
		}
		return planIdLocal.includes(planId)
	}

	featuresListFormatted(){
		return this.featuresDescription.reduce((acc, feature) => {
			return acc + feature.title + "<br/>"
		}, "")
	}

	getBillingPeriodFormatted(){
		if(this.isYearly()){
			return "year"
		}
		return "mo"
	}

	isYearly(){
		return this.billingPeriod === SubscriptionDurationEnum.YEAR
	}

	isMonthly(){
		return this.billingPeriod === SubscriptionDurationEnum.MONTH
	}

	getPriceAmount(){
		return this.pricePerMonth || this.price || this.nextPaymentAmount
	}

	getPriceFormatted(){
		let currencyItem = CurrencyItem.currencyByCode(this.currencyCode || this.currency)
		let price = this.getPriceAmount()
		if(price){
			return currencyItem.formatted(price)
		}
		return "Free"
	}

	getYearlySavings(){
		if(this.pricing && this.pricing.yearly && this.pricing.monthly){
			let yearlyPrice = this.pricing.yearly.price
			let monthlyPrice = this.pricing.monthly.price
			let yearlySaving = monthlyPrice * 12 - yearlyPrice
			return yearlySaving
		}
		return 0
	}

	getYearlySavingsFormatted(){
		let currencyItem = CurrencyItem.currencyByCode(this.currencyCode)
		let yearlySavings = this.getYearlySavings()
		if(yearlySavings){
			return currencyItem.formatted(yearlySavings)
		}
		return ""
	}




	getMonthlySubscription(){
		let subscription = classToClass(this)

		if(this.pricing.monthly){
			subscription.planId = this.pricing.monthly.planId
			subscription.billingPeriod = SubscriptionDurationEnum.MONTH
			subscription.price = this.pricing.monthly.price
			subscription.pricePerMonth = this.pricing.monthly.price
			subscription.billingNotePerMonth = "month"
			subscription.currencyCode = this.pricing.monthly.currencyCode
			return subscription
		}
	}



	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion)

		if(oldVersion < 2){
			if(this.features.clientLimit){
				this.features.activeClientLimit = this.features.clientLimit
			}
		}
	}


	getStorageConsumedSize(){
		return this.usageStats.storageConsumedSize
	}

	isEnoughStorageSpace(amountToAdd = 0){
		return this.getStorageConsumedSize() + amountToAdd < this.getMaxStorageSize()
	}

	getStorageUsagePercent(){
		return Math.floor(this.getStorageConsumedSize() / this.getMaxStorageSize() * 100)
	}

	isActiveClientsAtTheLimit(){
		if(this.isStudentPlan()){
			return false
		}
		return this.getActiveClientsLimit() !== ValuesEnum.NO_LIMIT
			&& this.usageStats.activeClientsCount === this.getActiveClientsLimit()
	}
	isActiveClientsOverTheLimit(){
		if(this.isStudentPlan()){
			return false
		}
		return this.getActiveClientsLimit() !== ValuesEnum.NO_LIMIT
			&& this.usageStats.activeClientsCount > this.getActiveClientsLimit()
	}
	isActiveClientsAtLimitOrMore(){
		return this.isActiveClientsAtTheLimit() || this.isActiveClientsOverTheLimit()
	}
	canAddMoreClients(){
		return !this.isActiveClientsAtLimitOrMore()
	}

	getActiveClientsLimit(){
		return this.features.activeClientLimit
	}


}
